/**
 * DeadZoneSkillEffect — Lightning tree Pulse path Fork 2: Dead Zone active.
 *
 * Drops a 2-tile radius zone of total EM suppression for 4 seconds. Inside
 * the zone: Conductor magic, Shield generators, and Officer auras are all
 * disabled. Enemies inside receive disruption every second. Consumes Static
 * charge on activation.
 *
 * The "suppression" of enemy special abilities is an integration-layer concern.
 * The zone type `'dead_zone'` is the signal: enemy AI queries
 * `ZoneSystem.isInsideType(x, y, 'dead_zone')` before activating specials
 * and skips if inside. The SkillEffect simply creates the zone with the
 * correct type and configuration.
 *
 * On activation:
 *   1. Fizzle check at early attunement XP.
 *   2. Break Hide.
 *   3. Resolve radius and duration from resolver (Blackout: +0.5 tiles, +2s).
 *   4. Query upgrade keys for exit suppression, feedback, signal decay.
 *   5. Consume Static charge (bonus damage applied by integration layer).
 *   6. Create zone via deps.zones.create() with full config.
 *   7. Start cooldown (1200ms, long tier for lightning tree).
 *
 * Upgrade key inheritance (resolver scope: 'dead_zone'):
 * - durationMs: additive — Blackout adds 2000ms (6s total).
 * - radiusTiles: additive — Blackout adds 0.5 tiles (2.5 total).
 * - exitSuppressionMs: additive — Blackout adds 2000ms disruption on exit.
 * - feedbackDamage: additive — Feedback Loop adds 20 flat damage.
 * - feedbackDisruptMs: additive — Feedback Loop adds 600ms disruption.
 * - exitMoveSpeedReduction: additive — Signal Decay adds 0.15 (15%).
 * - exitAttackSpeedReduction: additive — Signal Decay adds 0.2 (20%).
 * - exitSlowDurationMs: additive — Signal Decay adds 3000ms slow duration.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy — disruption has no immunity window)
 * @see lightning-spec.md § Dead Zone (`dead_zone`)
 * @see lightning-spec.md § Blackout (`dead_zone_upgrade_1`)
 * @see lightning-spec.md § Feedback Loop (`dead_zone_upgrade_2`)
 * @see lightning-spec.md § Signal Decay (`dead_zone_passive`)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone configuration
// ---------------------------------------------------------------------------

/**
 * Zone configuration passed to deps.zones.create() when Dead Zone fires.
 * Maps to ZoneSystem.ZoneConfig fields. The dotDefId 'disruption' causes the
 * integration layer to route dot_tick events to StatusEffectSystem.apply().
 *
 * Upgrade fields (exitSuppressionMs, feedbackDamage, etc.) are 0 when the
 * corresponding upgrade is not unlocked. The integration layer checks these
 * values and wires callbacks only when non-zero.
 */
export interface DeadZoneZoneConfig {
  readonly type: 'dead_zone';
  readonly x: number;
  readonly y: number;
  readonly radiusTiles: number;
  readonly durationMs: number;
  /** Tick interval in ms for periodic disruption application. Spec: 1000ms. */
  readonly tickIntervalMs: number;
  /** Status effect def ID applied on each tick ('disruption'). */
  readonly dotDefId: string;
  /** Duration of each disruption tick in ms. Spec: 1200ms (> tick interval). */
  readonly dotDurationMs: number;
  readonly sourceSkillId: string;
  /** Blackout: disruption duration applied when enemy exits zone. 0 = inactive. */
  readonly exitSuppressionMs: number;
  /** Feedback Loop: flat damage when enemy attempts ability inside zone. 0 = inactive. */
  readonly feedbackDamage: number;
  /** Feedback Loop: disruption duration on ability attempt. 0 = inactive. */
  readonly feedbackDisruptMs: number;
  /** Signal Decay: move speed reduction fraction on zone exit. 0 = inactive. */
  readonly exitMoveSpeedReduction: number;
  /** Signal Decay: attack speed reduction fraction on zone exit. 0 = inactive. */
  readonly exitAttackSpeedReduction: number;
  /** Signal Decay: duration of exit slow debuffs in ms. 0 = inactive. */
  readonly exitSlowDurationMs: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface DeadZoneDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  /**
   * Zone system interface. Dead Zone always creates a zone on activation.
   * The zone's dotDefId 'disruption' causes the integration layer to route
   * periodic dot_tick events to StatusEffectSystem.apply('disruption', ...).
   */
  readonly zones: {
    create(config: DeadZoneZoneConfig): void;
  };
  readonly charge: {
    /** Consume all Static charge. Returns the amount consumed (0 if empty). */
    consume(chargeId: string): number;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
  readonly breakHide?: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface DeadZoneConfig {
  /** Long cooldown tier: 1200ms (lightning tree). */
  readonly cooldownMs: number;
  /** Base zone radius in tiles. Spec: 2 tiles. */
  readonly baseRadiusTiles: number;
  /** Base zone duration in ms. Spec: 4000ms. */
  readonly baseDurationMs: number;
  /** Tick interval for periodic disruption inside zone. Spec: 1000ms. */
  readonly tickIntervalMs: number;
  /** Duration of each disruption tick. Spec: 1200ms (slightly > tick interval). */
  readonly disruptionDurationMs: number;
}

const DEFAULT_CONFIG: DeadZoneConfig = {
  cooldownMs: 1200,
  baseRadiusTiles: 2,
  baseDurationMs: 4000,
  tickIntervalMs: 1000,
  disruptionDurationMs: 1200,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class DeadZoneSkillEffect implements SkillEffect {
  readonly skillId = 'dead_zone';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'MISS';
  readonly failLabel = 'FIZZLE';

  private readonly deps: DeadZoneDeps;
  private readonly config: DeadZoneConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _zoneSpawns = 0;
  private _totalChargeConsumed = 0;
  private _resolvedRadiusTiles = 0;
  private _resolvedDurationMs = 0;

  constructor(deps: DeadZoneDeps, config: Partial<DeadZoneConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (for simulation assertions) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get zoneSpawns(): number { return this._zoneSpawns; }
  get totalChargeConsumed(): number { return this._totalChargeConsumed; }
  get resolvedRadiusTiles(): number { return this._resolvedRadiusTiles; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — early attunement XP produces a visible flicker but no zone.
    if (!this.deps.rollSkillSuccess('dead_zone')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break Hide on activation.
    this.deps.breakHide?.();

    // Resolve upgrade-modified stats (scope: 'dead_zone').
    this._resolvedRadiusTiles =
      this.config.baseRadiusTiles +
      this.deps.resolver.getAdditiveEffect('dead_zone', 'radiusTiles');

    this._resolvedDurationMs =
      this.config.baseDurationMs +
      this.deps.resolver.getAdditiveEffect('dead_zone', 'durationMs');

    // Query upgrade keys — return 0 when upgrades are not unlocked.
    const exitSuppressionMs =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'exitSuppressionMs');
    const feedbackDamage =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'feedbackDamage');
    const feedbackDisruptMs =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'feedbackDisruptMs');
    const exitMoveSpeedReduction =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'exitMoveSpeedReduction');
    const exitAttackSpeedReduction =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'exitAttackSpeedReduction');
    const exitSlowDurationMs =
      this.deps.resolver.getAdditiveEffect('dead_zone', 'exitSlowDurationMs');

    // Consume Static charge — bonus damage applied by integration layer.
    const consumed = this.deps.charge.consume('static_charge');
    this._totalChargeConsumed += consumed;

    // Create the suppression zone.
    this.deps.zones.create({
      type: 'dead_zone',
      x: 0, // Integration layer provides actual placement coordinates.
      y: 0,
      radiusTiles: this._resolvedRadiusTiles,
      durationMs: this._resolvedDurationMs,
      tickIntervalMs: this.config.tickIntervalMs,
      dotDefId: 'disruption',
      dotDurationMs: this.config.disruptionDurationMs,
      sourceSkillId: 'dead_zone',
      exitSuppressionMs,
      feedbackDamage,
      feedbackDisruptMs,
      exitMoveSpeedReduction,
      exitAttackSpeedReduction,
      exitSlowDurationMs,
    });
    this._zoneSpawns++;
    this._activations++;

    // Start cooldown.
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle state.
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
